import { useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../utils/googleMaps";
import { MapPinIcon, XMarkIcon, ViewfinderCircleIcon } from "@heroicons/react/24/outline";
import { auth } from "../firebase";
import { toast } from "react-toastify";
import {
  MAP_CENTER,
  FOCUS_RADIUS_MILES,
  MAP_OPTIONS,
  ORIGIN_STORAGE_KEY,
  getSelectedMarkerIcon,
  getDistanceMiles,
  formatMiles,
  anchorFromStored,
  slotToOrigin,
  readStoredLocation,
  resolveIpLocation,
  getGrantedBrowserPosition,
} from "./eventSuggestionsHelpers";
import { SuggestionCard } from "./SuggestionCard";
import PlacesAPI from "../api/PlacesAPI";

export const MapView = ({ suggestions, onUpdateStatus, isSuperAdmin, vendorID, hosts, usersById }) => {
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const [selectedId, setSelectedId] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  // Reference point for distances: host's current location or a custom one
  const [origin, setOrigin] = useState(null);
  const [originLabel, setOriginLabel] = useState("");
  const [locating, setLocating] = useState(false);
  const mapRef = useRef(null);

  const applyOrigin = (coords, label, mode) => {
    setOrigin(coords);
    setOriginLabel(label);
    try {
      if (mode === "custom") {
        localStorage.setItem(ORIGIN_STORAGE_KEY, JSON.stringify({ ...coords, label }));
      } else {
        localStorage.removeItem(ORIGIN_STORAGE_KEY);
      }
    } catch { /* storage unavailable */ }
  };

  const clearOrigin = () => {
    setOrigin(null);
    setOriginLabel("");
    try {
      localStorage.removeItem(ORIGIN_STORAGE_KEY);
    } catch { /* storage unavailable */ }
  };

  // Zoom/pan the map so roughly `miles` radius around `coords` is in view.
  // Uses fitBounds (vs. a hardcoded zoom) so the radius stays consistent across
  // map sizes. Latitude is ~69 mi/deg; longitude shrinks by cos(lat).
  const focusRadiusMiles = (coords, miles = FOCUS_RADIUS_MILES) => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const latDelta = miles / 69;
    const lngDelta = miles / (69 * Math.cos((coords.lat * Math.PI) / 180));
    const bounds = new window.google.maps.LatLngBounds(
      { lat: coords.lat - latDelta, lng: coords.lng - lngDelta },
      { lat: coords.lat + latDelta, lng: coords.lng + lngDelta }
    );
    map.fitBounds(bounds);
  };

  const handleUseMyLocation = (silent = false) => {
    if (!navigator.geolocation) {
      if (!silent) toast.error("Geolocation is not supported by your browser");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        applyOrigin(coords, "My location", "current");
        focusRadiusMiles(coords);
      },
      (err) => {
        setLocating(false);
        if (silent) return;
        toast.error(
          err.code === 1
            ? "Location access denied. Enable it in your browser settings, then try again."
            : "Could not get your location. Try setting a custom one instead."
        );
      },
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 }
    );
  };

  // On mount: restore a saved custom location; otherwise auto-detect the admin's
  // area the way the Discover page does — precise browser location if it was
  // already granted, then their stored location, then an IP lookup — so the map
  // opens on their own city instead of the hardcoded SF Bay default. None of the
  // fallbacks trigger a permission prompt.
  useEffect(() => {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(ORIGIN_STORAGE_KEY));
    } catch { /* corrupt or unavailable storage */ }
    if (saved && typeof saved.lat === "number" && typeof saved.lng === "number") {
      setOrigin({ lat: saved.lat, lng: saved.lng });
      setOriginLabel(saved.label || "Custom location");
      return;
    }

    let cancelled = false;
    (async () => {
      // 1. Precise current location, but only if already granted (no prompt).
      const precise = await getGrantedBrowserPosition();
      if (cancelled) return;
      if (precise) {
        applyOrigin(precise, "My location", "current");
        return;
      }
      // 2. Stored location (set by Discover or a prior detect), like the PWA.
      const storedOrigin = slotToOrigin(anchorFromStored(await readStoredLocation(auth.currentUser?.uid)));
      if (cancelled) return;
      if (storedOrigin) {
        applyOrigin(storedOrigin.coords, storedOrigin.label, "detected");
        return;
      }
      // 3. IP lookup — the Discover fallback that needs no permission.
      const ipOrigin = slotToOrigin(anchorFromStored(await resolveIpLocation()));
      if (cancelled) return;
      if (ipOrigin) applyOrigin(ipOrigin.coords, ipOrigin.label, "detected");
      // else: leave the map on its SF Bay default with no origin set.
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the map centered on the origin whenever it changes
  useEffect(() => {
    if (origin && mapRef.current) mapRef.current.panTo(origin);
  }, [origin]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setSelectedId(null);
      setIsClosing(false);
    }, 280);
  };

  if (loadError) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/40 p-6 text-sm text-red-700 dark:text-red-300">
        Failed to load Google Maps. Please check your API key.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-[600px] rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const mapped = suggestions.filter((s) => s.coords);
  const unmapped = suggestions.filter((s) => s.venue && !s.coords);
  const selectedSuggestion = suggestions.find((s) => s.id === selectedId);

  return (
    <div>
      {/* Distance origin: my location / custom location */}
      <LocationBar
        origin={origin}
        originLabel={originLabel}
        locating={locating}
        onUseMyLocation={() => handleUseMyLocation(false)}
        onPickCustom={(label, coords) => {
          applyOrigin(coords, label, "custom");
          focusRadiusMiles(coords);
        }}
        onClear={clearOrigin}
      />

      {/* Map + card container */}
      <div
        className="relative lg:flex lg:gap-4 lg:items-stretch h-[520px] sm:h-[580px] lg:h-[640px]"
      >
        {/* Map — fills container on mobile, flex-1 on desktop */}
        <div className="absolute inset-0 lg:relative lg:inset-auto lg:flex-1 lg:min-w-0 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-300">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={MAP_CENTER}
            zoom={9}
            options={MAP_OPTIONS}
            onClick={() => handleClose()}
            onLoad={(map) => {
              mapRef.current = map;
              if (origin) map.panTo(origin);
            }}
            onUnmount={() => { mapRef.current = null; }}
          >
            {mapped.map((s) => {
              const isSelected = s.id === selectedId;
              return (
                <Marker
                  key={s.id}
                  position={s.coords}
                  title={
                    origin
                      ? `${s.eventName || s.venue} · ${formatMiles(getDistanceMiles(origin, s.coords))} away`
                      : (s.eventName || s.venue)
                  }
                  onClick={() => setSelectedId(s.id)}
                  icon={isSelected ? getSelectedMarkerIcon() : undefined}
                  zIndex={isSelected ? 999 : undefined}
                />
              );
            })}
            {origin && (
              <Marker
                position={origin}
                draggable
                onClick={() => handleClose()}
                onDragEnd={(e) =>
                  applyOrigin(
                    { lat: e.latLng.lat(), lng: e.latLng.lng() },
                    "Dropped pin",
                    "custom"
                  )
                }
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: "#4285F4",
                  fillOpacity: 1,
                  strokeColor: "#ffffff",
                  strokeWeight: 2.5,
                }}
                title={`${originLabel} — drag to adjust`}
                zIndex={1000}
              />
            )}
          </GoogleMap>
        </div>

        {/* Dim overlay on mobile when card is open */}
        {(selectedSuggestion || isClosing) && (
          <div
            className={`fixed inset-0 z-[9] lg:hidden transition-opacity duration-200 bg-black/30 ${isClosing ? "opacity-0" : "opacity-100 animate-fade-in"
              }`}
            onClick={handleClose}
          />
        )}

        {/* Card — bottom sheet overlay on mobile, side panel on desktop */}
        {(selectedSuggestion || isClosing) && (
          <div
            className={[
              "bg-white dark:bg-gray-800 flex flex-col z-10",
              "fixed inset-x-0 bottom-0 max-h-[78%] rounded-t-2xl shadow-2xl",
              "lg:relative lg:inset-auto lg:max-h-none lg:h-full lg:w-80 lg:shrink-0 lg:rounded-xl lg:border lg:border-gray-200/80 dark:lg:border-gray-700 lg:shadow-lg",
              isClosing
                ? "opacity-0 translate-y-4 lg:translate-y-0 lg:translate-x-2 transition-all duration-200 ease-in"
                : "animate-slide-up lg:animate-slide-in-right",
            ].join(" ")}
          >
            {/* Mobile-only: drag handle */}
            <div className="lg:hidden flex justify-center pt-2.5 pb-1 shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>

            {/* Polished header */}
            <div className="relative flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-800">
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide leading-none mb-0.5">
                  Event Idea
                </p>
              </div>
              <button
                onClick={handleClose}
                className="shrink-0 rounded-full p-1.5 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto flex-1 overscroll-contain">
              {selectedSuggestion && (
                <SuggestionCard
                  suggestion={selectedSuggestion}
                  onUpdateStatus={onUpdateStatus}
                  isSuperAdmin={isSuperAdmin}
                  vendorID={vendorID}
                  hosts={hosts}
                  usersById={usersById}
                  origin={origin}
                  mapMode
                />
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
        <span>{mapped.length} of {suggestions.filter((s) => s.venue).length} venues mapped</span>
        {unmapped.length > 0 && (
          <span>{unmapped.length} venue{unmapped.length > 1 ? "s" : ""} could not be located</span>
        )}
      </div>
    </div>
  );
};

// Location bar — pick the origin point that distances are measured from
export const LocationBar = ({ origin, originLabel, locating, onUseMyLocation, onPickCustom, onClear }) => {
  const [searchText, setSearchText] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [resolving, setResolving] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowPredictions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const suggestions = await PlacesAPI.autocomplete(value);
      setPredictions(suggestions);
      setShowPredictions(suggestions.length > 0);
    }, 300);
  };

  const handleSelect = async (prediction) => {
    const pred = prediction.placePrediction;
    if (!pred?.placeId) return;
    const label = pred?.structuredFormat?.mainText?.text || pred?.text?.text || "Custom location";
    setShowPredictions(false);
    setPredictions([]);
    setSearchText("");
    setResolving(true);
    const coords = await PlacesAPI.getLocation(pred.placeId);
    setResolving(false);
    if (coords) {
      onPickCustom(label, coords);
    } else {
      toast.error("Could not locate that place");
    }
  };

  return (
    <div className="mb-2 flex items-center gap-2">
      <button
        type="button"
        onClick={onUseMyLocation}
        disabled={locating}
        title="Measure distances from my current location"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-white dark:bg-gray-800 px-2.5 py-2 text-xs font-semibold text-gray-700 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {locating ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 dark:border-gray-400" />
        ) : (
          <ViewfinderCircleIcon className="h-4 w-4" />
        )}
        <span className="hidden sm:inline">My location</span>
      </button>

      <div ref={containerRef} className="relative flex-1 min-w-0 max-w-xs">
        <input
          type="text"
          value={searchText}
          onChange={handleSearchChange}
          onFocus={() => predictions.length > 0 && setShowPredictions(true)}
          placeholder="Set a custom location…"
          className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 pl-3 pr-8 py-2 text-xs text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {resolving && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary" />
        )}
        {showPredictions && (
          <ul className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1">
            {predictions.slice(0, 5).map((p, i) => {
              const pred = p.placePrediction;
              const main = pred?.structuredFormat?.mainText?.text || pred?.text?.text || "";
              const secondary = pred?.structuredFormat?.secondaryText?.text || "";
              return (
                <li key={pred?.placeId || i}>
                  <button
                    type="button"
                    onClick={() => handleSelect(p)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <span className="block text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{main}</span>
                    {secondary && (
                      <span className="block text-[11px] text-gray-400 dark:text-gray-500 truncate">{secondary}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {origin && (
        <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
          <MapPinIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate max-w-[140px]">{originLabel}</span>
          <button
            type="button"
            onClick={onClear}
            title="Clear location"
            className="shrink-0 hover:text-primaryDark"
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        </span>
      )}
    </div>
  );
};

